import { useEffect } from 'react';

interface PerformanceMetrics {
  fcp: number;
  lcp: number;
  fid: number;
  cls: number;
  ttfb: number;
}

export const usePerformance = () => {
  useEffect(() => {
    // Monitor Core Web Vitals
    const reportWebVitals = (metric: any) => {
      // Silently send to analytics (no console logging to reduce noise)
      if (typeof window !== 'undefined' && window.gtag) {
        try {
          // Check if gtag is actually a function before calling
          if (typeof window.gtag === 'function') {
            window.gtag('event', metric.name, {
              event_category: 'Web Vitals',
              value: Math.round(metric.value),
              event_label: metric.id,
              non_interaction: true,
            });
          }
        } catch (e) {
          // Silently ignore analytics errors (network issues, ad blockers, etc.)
          // These are expected in some environments and don't affect functionality
        }
      }
    };

    // Monitor First Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'paint') {
              if (entry.name === 'first-contentful-paint') {
                reportWebVitals({
                  name: 'FCP',
                  value: entry.startTime,
                  id: 'fcp'
                });
              }
            }
          }
        });
        observer.observe({ entryTypes: ['paint'] });
      } catch (e) {
        console.warn('Performance Observer not supported');
      }
    }

    // Monitor Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          reportWebVitals({
            name: 'LCP',
            value: lastEntry.startTime,
            id: 'lcp'
          });
        });
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP Observer not supported');
      }
    }

    // Monitor Cumulative Layout Shift
    if ('PerformanceObserver' in window) {
      try {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value;
            }
          }
          reportWebVitals({
            name: 'CLS',
            value: clsValue,
            id: 'cls'
          });
        });
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('CLS Observer not supported');
      }
    }

    // Monitor First Input Delay
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            reportWebVitals({
              name: 'FID',
              value: (entry as any).processingStart - entry.startTime,
              id: 'fid'
            });
          }
        });
        observer.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.warn('FID Observer not supported');
      }
    }

    // Monitor Time to First Byte
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const ttfb = (entry as any).responseStart - (entry as any).requestStart;
              reportWebVitals({
                name: 'TTFB',
                value: ttfb,
                id: 'ttfb'
              });
            }
          }
        });
        observer.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        console.warn('TTFB Observer not supported');
      }
    }

  }, []);

  return null;
};

// Performance optimization utilities
export const optimizeImages = () => {
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    if (!img.loading) {
      img.loading = 'lazy';
    }
    if (!img.decoding) {
      img.decoding = 'async';
    }
  });
};

export const preloadCriticalResources = () => {
  const criticalResources = [
    '/src/main.tsx',
    '/src/assets/ai-career-platform-logo.png'
  ];

  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    link.as = resource.endsWith('.tsx') || resource.endsWith('.js') ? 'script' : 'image';
    document.head.appendChild(link);
  });
};

export const deferNonCriticalScripts = () => {
  const scripts = document.querySelectorAll('script[src]');
  scripts.forEach(script => {
    if (!script.async && !script.defer) {
      script.defer = true;
    }
  });
};
